import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientSelfDataContext } from '../../../context/PatientSelfDataContext';
import { useAuth } from '../../../hooks/useAuth';
import { getPatientDashboardPath } from '../../../utils/informantDashboardRouting';
import { auth } from '../../../firebase';
import PatientHeader from './components/PatientHeader';
import QuestionnaireAccordion from './components/QuestionnaireAccordion';
import InformantSetupForm from './components/InformantSetupForm';
import PatientManageInformantsForm from './components/PatientManageInformantsForm';
import AllFormsCompletedFeedbackModal from '../3_forms/components/AllFormsCompletedFeedbackModal';

export default function PatientDashboard() {
  const { patientDataModel, refreshPatientData } = useContext(PatientSelfDataContext);
  const { claims } = useAuth();
  const navigate = useNavigate();

  // Adult Autism (and future modular) patients belong on the modular dashboard,
  // which reads from the formInstances subcollection. This is the same guard
  // InformantDashboard applies for autism informants. It matters because some
  // entry points (e.g. older questionnaire reminder emails) still hard-link to
  // this legacy ADHD dashboard; without this redirect an autism patient would
  // see an empty form list here. getPatientDashboardPath returns the current
  // (legacy) path for ADHD/unknown patients, so they never redirect.
  useEffect(() => {
    const path = getPatientDashboardPath(claims);
    if (path !== '/patient-dashboard') {
      navigate(path, { replace: true });
    }
  }, [claims, navigate]);

  // Tracks whether the mount-time refreshPatientData() call has completed.
  // Until the fresh Firestore data arrives, the dashboard shows a skeleton
  // instead of rendering from stale localStorage — which can briefly show
  // "Add your informants" even after the user has already completed that step.
  const [isRefreshing, setIsRefreshing] = useState(true);

  // Fetch fresh data on mount so the dashboard reflects progress from any
  // form saves made since the context was last loaded.  Without onSnapshot
  // this is the only point where updated progress flows into the UI.
  useEffect(() => {
    refreshPatientData().finally(() => setIsRefreshing(false));
  }, [refreshPatientData]);

// Add state for feedback modal
const [showFeedbackModal, setShowFeedbackModal] = useState(false);

// Add state for informant setup modal
const [showInformantSetup, setShowInformantSetup] = useState(false);

// Add state for manage informants modal
const [showManageInformants, setShowManageInformants] = useState(false);

// Add state to track user's organization
const [userOrganisation, setUserOrganisation] = useState(null);

  // Refs for auto-scroll functionality
  const scrollContainerRef = useRef(null);
  const nextQuestionnaireRef = useRef(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Get user's organization from Firebase Auth
  useEffect(() => {
    const getUserOrganisation = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdTokenResult();
          setUserOrganisation(token.claims.organisation);
        } catch (error) {
          console.error('Error getting user organisation:', error);
          setUserOrganisation(null);
        }
      }
    };
    getUserOrganisation();
  }, []);

  // Custom title overrides for specific forms
  const getTitleOverride = (originalTitle) => {
    switch (originalTitle) {
      case 'Your Family':
        return 'Your Family\'s Health';
      case 'Your Information':
        return 'About You';
      default:
        return originalTitle;
    }
  };  

  // Generate assessment config from patient data model
  const assessmentsConfig = useMemo(() => {
    if (!patientDataModel?.dataModel?.['1_preAppointmentInformation']?.sections) {
      return [];
    }
    
    // Find the Patient History section
    const patientHistorySection = Object.values(patientDataModel.dataModel['1_preAppointmentInformation'].sections)
      .find(section => section.internalName === "Patient History");
    
    // Find the Patient Scales section
    const patientScalesSection = Object.values(patientDataModel.dataModel['1_preAppointmentInformation'].sections)
      .find(section => section.internalName === "Patient Scales");
    
    
    // Initialize array for all domains
  let allDomains = [];
  
  // Add Patient History domains if they exist
  if (patientHistorySection && patientHistorySection.domains) {
    const historyDomains = Object.values(patientHistorySection.domains)
      .sort((a, b) => a.suggestedOrder - b.suggestedOrder) // Sort by suggested order
      .map(domain => {
        // Get form progress from patient data
        const formData = patientDataModel?.formData?.[domain.varName] || {};
        const progress = formData.progress || 0;
        
        return {
          title: getTitleOverride(domain.displayName),
          subtitle: domain.displaySubtitle,
          route: domain.route,
          formKey: domain.varName,
          progress: progress,
          type: 'history'
        };
      });
    
    allDomains = [...historyDomains];
  }
  
  // Add Patient Scales domains if they exist
  if (patientScalesSection && patientScalesSection.domains) {
    const scalesDomains = Object.values(patientScalesSection.domains)
      .sort((a, b) => a.suggestedOrder - b.suggestedOrder) // Sort by suggested order
      .map(domain => {
        // Get form progress from patient data
        const formData = patientDataModel?.formData?.[domain.varName] || {};
        const progress = formData.progress || 0;
        
        // Get scale results if available
        const scaleResults = formData.scaleResults || null;
        
        return {
          title: getTitleOverride(domain.displayName),
          subtitle: domain.displaySubtitle || "Standardised assessment scale",
          route: domain.route,
          formKey: domain.varName,
          progress: progress,
          type: 'scale',
          scaleResults: scaleResults
        };
      });
    
    // Add scales to the domains array
    allDomains = [...allDomains, ...scalesDomains];
  }
  
  return allDomains;
  }, [patientDataModel]);

   // Create assessments with actual progress from Firestore
   const assessments = useMemo(() => {
    return assessmentsConfig.map(assessment => {
      const formData = patientDataModel?.formData?.[assessment.formKey];
      let progress = 0;
      
      // If we have form data with progress, use it
      if (formData?.progress?.progressPercentage !== undefined) {
        progress = formData.progress.progressPercentage;
      }
      
      // Format progress to be between 0 and 100
      progress = Math.min(Math.max(Math.round(progress), 0), 100);
      
      return {
        ...assessment,
        progress
      };
    });
  }, [patientDataModel, assessmentsConfig]);

  // Group related questionnaires and create hierarchical structure
  const groupedAssessments = useMemo(() => {
    const grouped = [];
    
    // Check if this is ThinkDivergent organization - use different grouping
    if (userOrganisation === 'ThinkDivergent') {
      // ThinkDivergent specific grouping
      const backgroundForms = assessments.filter(assessment => 
        assessment.title.toLowerCase().includes('about you') ||
        assessment.formKey === 'yourInformationSubclinical'
      );
      
      const symptomForms = assessments.filter(assessment => 
        assessment.formKey === 'bdefs' || assessment.formKey === 'weiss'
      );
      
      // Helper function to create a group
      const createGroup = (title, children, groupKey, groupType = 'history-group') => {
        if (children.length === 0) return null;
        
        const totalProgress = children.reduce((sum, child) => sum + child.progress, 0);
        const combinedProgress = Math.round(totalProgress / children.length);
        
        return {
          isGroup: true,
          title,
          progress: combinedProgress,
          type: groupType,
          formKey: groupKey,
          route: null,
          children: children.sort((a, b) => (a.suggestedOrder || 0) - (b.suggestedOrder || 0))
        };
      };
      
      // Add Your Information group
      const backgroundGroup = createGroup('Your Information', backgroundForms, 'backgroundInfo');
      if (backgroundGroup) {
        grouped.push(backgroundGroup);
      }
      
      // Add Your Experiences group
      const symptomsGroup = createGroup('Your Experiences', symptomForms, 'yourSymptoms', 'scale-group');
      if (symptomsGroup) {
        grouped.push(symptomsGroup);
      }
      
      // Add any remaining forms as individual items
      const groupedFormKeys = new Set([
        ...backgroundForms.map(f => f.formKey),
        ...symptomForms.map(f => f.formKey)
      ]);
      
      assessments.forEach(assessment => {
        if (!groupedFormKeys.has(assessment.formKey)) {
          grouped.push({
            ...assessment,
            isGroup: false
          });
        }
      });
      
    } else {
      // Existing grouping logic for other organizations
      
      // Separate assessments by type
      const historyAssessments = [];
      const adhdScales = [];
      const otherScales = [];
      
      // ADHD symptom rating scale form keys - includes both patient self-report variants:
      // - baarsIVCurrent/baarsIVChildhood: Default for most organisations
      // - asrs/baarsIVChildhood: Used by A&A Associates (asrs for adulthood, BAARS-IV for childhood)
      const adhdScaleFormKeys = ['baarsIVCurrent', 'baarsIVChildhood', 'asrs'];
      
      assessments.forEach((assessment) => {
        if (assessment.type === 'history') {
          historyAssessments.push(assessment);
        } else if (adhdScaleFormKeys.includes(assessment.formKey)) {
          adhdScales.push(assessment);
        } else if (assessment.type === 'scale') {
          otherScales.push(assessment);
        }
      });
      
      // Helper function to create a group
      const createGroup = (title, children, groupKey) => {
        if (children.length === 0) return null;
        
        const totalProgress = children.reduce((sum, child) => sum + child.progress, 0);
        const combinedProgress = Math.round(totalProgress / children.length);
        
        return {
          isGroup: true,
          title,
          progress: combinedProgress,
          type: 'history-group',
          formKey: groupKey,
          route: null,
          children: children.sort((a, b) => (a.suggestedOrder || 0) - (b.suggestedOrder || 0))
        };
      };
      
      // Helper function to find forms by title keywords
      const findFormsByKeywords = (keywords) => {
        return historyAssessments.filter(assessment => 
          keywords.some(keyword => 
            assessment.title.toLowerCase().includes(keyword.toLowerCase())
          )
        );
      };
      
      // Group history forms
      const currentConcernsForms = findFormsByKeywords(['information', 'about you', 'current concerns']);
      const childhoodForms = findFormsByKeywords(['childhood']);
      const adultLifeForms = findFormsByKeywords(['adult life', 'lifestyle', 'law']);
      const healthForms = findFormsByKeywords(['health', 'wellbeing', 'family', 'family\'s health']);
      
      // Track which forms have been grouped
      const groupedFormKeys = new Set();
      
      // Add Current Concerns group
      const currentConcernsGroup = createGroup('Current Concerns', currentConcernsForms, 'currentConcerns');
      if (currentConcernsGroup) {
        grouped.push(currentConcernsGroup);
        currentConcernsForms.forEach(form => groupedFormKeys.add(form.formKey));
      }
      
      // Add Childhood group
      const childhoodGroup = createGroup('Childhood', childhoodForms, 'childhood');
      if (childhoodGroup) {
        grouped.push(childhoodGroup);
        childhoodForms.forEach(form => groupedFormKeys.add(form.formKey));
      }
      
      // Add Adult Life group
      const adultLifeGroup = createGroup('Adult Life', adultLifeForms, 'adultLife');
      if (adultLifeGroup) {
        grouped.push(adultLifeGroup);
        adultLifeForms.forEach(form => groupedFormKeys.add(form.formKey));
      }
      
      // Add Health and Wellbeing group
      const healthGroup = createGroup('Health and Wellbeing', healthForms, 'healthWellbeing');
      if (healthGroup) {
        grouped.push(healthGroup);
        healthForms.forEach(form => groupedFormKeys.add(form.formKey));
      }
      
      // Add any remaining ungrouped history forms as individual items
      historyAssessments.forEach(assessment => {
        if (!groupedFormKeys.has(assessment.formKey)) {
          grouped.push({
            ...assessment,
            isGroup: false
          });
        }
      });
      
      // Create ADHD symptoms group if we have both adulthood and childhood questionnaires
      // Adulthood can be either baarsIVCurrent (default) or asrs (A&A Associates)
      // Childhood is always baarsIVChildhood
      if (adhdScales.length === 2) {
        const adulthood = adhdScales.find(a => a.formKey === 'baarsIVCurrent' || a.formKey === 'asrs');
        const childhood = adhdScales.find(a => a.formKey === 'baarsIVChildhood');
        
        if (adulthood && childhood) {
          const adhdGroup = createGroup('Your ADHD Symptoms', [adulthood, childhood], 'adhdSymptoms');
          if (adhdGroup) {
            adhdGroup.type = 'scale-group';
            grouped.push(adhdGroup);
          }
        }
      }
      
      // Create Other Symptoms group if we have other scales
      if (otherScales.length > 0) {
        const otherSymptomsGroup = createGroup('Other Symptoms', otherScales, 'otherSymptoms');
        if (otherSymptomsGroup) {
          otherSymptomsGroup.type = 'scale-group';
          grouped.push(otherSymptomsGroup);
        }
      }
    }
    
    return grouped;
  }, [assessments, userOrganisation]);

  // Sort the grouped assessments by suggested order
  const sortedAssessments = useMemo(() => {
    const sorted = [...groupedAssessments].sort((a, b) => {
      // For groups, use the minimum suggested order of their children
      const aOrder = a.isGroup ? 
        Math.min(...a.children.map(child => child.suggestedOrder || 0)) : 
        (a.suggestedOrder || 0);
      const bOrder = b.isGroup ? 
        Math.min(...b.children.map(child => child.suggestedOrder || 0)) : 
        (b.suggestedOrder || 0);
      
      return aOrder - bOrder;
    });
    
    return sorted;
  }, [groupedAssessments]);

  // Find the next questionnaire that needs attention (updated for groups)
  const nextQuestionnaireIndex = useMemo(() => {
    // Skip informant setup requirement for ThinkDivergent organization
    const requiresInformantSetup = userOrganisation !== 'ThinkDivergent';
    
    if (requiresInformantSetup && !patientDataModel?.hasCompletedInformantSetupForm) {
      return -1; // Special case for informant setup
    }
    
    // Find first questionnaire or group that's not completed
    const nextIndex = sortedAssessments.findIndex(assessment => {
      if (assessment.isGroup) {
        // Group is not complete if any child is not complete
        return assessment.children.some(child => child.progress < 100);
      } else {
        // Individual questionnaire
        return assessment.progress < 100;
      }
    });
    
    return nextIndex;
  }, [sortedAssessments, patientDataModel?.hasCompletedInformantSetupForm, userOrganisation]);

  // Check if feedback modal should be shown
  const shouldShowFeedbackModal = useMemo(() => {
    if (!patientDataModel || !assessments.length) return false;
    
    // Check if all forms are completed (100% progress)
    const allFormsCompleted = assessments.every(assessment => assessment.progress === 100);
    
    // Check if feedback has already been submitted
    const feedbackSubmitted = patientDataModel.formFeedback?.submittedAt;
    
    // Show modal if all forms completed but no feedback submitted
    return allFormsCompleted && !feedbackSubmitted;
  }, [patientDataModel, assessments]);

  // Track when data is loaded
  useEffect(() => {
    if (patientDataModel && assessments.length > 0) {
      setIsDataLoaded(true);
    }
  }, [patientDataModel, assessments]);

  // Show feedback modal when conditions are met
  useEffect(() => {
    if (shouldShowFeedbackModal && isDataLoaded) {
      // Small delay to ensure dashboard is fully loaded
      const timer = setTimeout(() => {
        setShowFeedbackModal(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [shouldShowFeedbackModal, isDataLoaded]);

  // Auto-scroll to next questionnaire when data is loaded
  useEffect(() => {
    if (!isDataLoaded || !scrollContainerRef.current) return;

    // Use a timeout to ensure DOM is fully rendered and refs are available
    const scrollTimeout = setTimeout(() => {
      if (nextQuestionnaireRef.current && scrollContainerRef.current) {
        // Calculate the scroll position with offset
        const targetElement = nextQuestionnaireRef.current;
        const container = scrollContainerRef.current;
        
        // Get the target element's position relative to the container
        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Calculate scroll position with 16px offset
        const scrollOffset = 16;
        const scrollPosition = container.scrollTop + targetRect.top - containerRect.top - scrollOffset;
        
        // Smooth scroll to the calculated position
        container.scrollTo({
          top: Math.max(0, scrollPosition), // Ensure we don't scroll past the top
          behavior: 'smooth'
        });
      }
    }, 300); // Small delay to ensure everything is rendered

    return () => clearTimeout(scrollTimeout);
  }, [isDataLoaded, nextQuestionnaireIndex]);  


                                      
                                      return (
    <div className="h-screen bg-white flex flex-col">
      {/* Fixed Header with Shadow Overlay */}
      <div className="flex-shrink-0 relative z-10">
        <PatientHeader 
          assessments={assessments} 
          onInformantsClick={() => setShowManageInformants(true)}
        />
        
        {/* White gradient shadow overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none transform translate-y-full z-20">
          {/* Drop shadow */}
          {/* <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-transparent"></div> */}
          {/* White fade */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-transparent"></div>
        </div>
      </div>
      
      {/* Scrollable Content Area */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto relative z-0"
      >
        {/* Progress Bar - Hidden for now */}
        {/* 
<div className="py-2 px-6 md:px-8">
  {assessments.length > 0 && (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm py-4 px-6 w-full text-left">
      <div className="flex justify-between items-center mb-2 w-full">
        <h3 className="text-lg font-semibold text-gray-800">Overall Progress</h3>
      </div>
      
      <div className="w-full self-stretch py-1.5 justify-start items-center gap-3 inline-flex">
        <div className="flex-grow h-2 relative rounded-lg bg-gray-200">
          <div
            className="absolute top-0 left-0 h-2 rounded-full transition-all duration-300 bg-green-500"
            style={{ 
              width: `${(assessments.filter(a => a.progress === 100).length / assessments.length) * 100}%` 
            }}
          />
        </div>
        <div className="text-slate-700 text-sm font-medium leading-tight">
          {Math.round((assessments.filter(a => a.progress === 100).length / assessments.length) * 100)}%
        </div>
      </div>
    </div>
  )}
</div>
        */}

        {/* Skeleton: accordion card placeholders — fades out once fresh data loads */}
        <div className={`px-4 pt-4 space-y-3 transition-opacity duration-300 ease-in-out ${
          isRefreshing ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        }`}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-2/5 rounded bg-gray-200" />
                  <div className="h-3 w-1/3 rounded bg-gray-100" />
                </div>
                <div className="h-8 w-8 rounded-full bg-gray-200" />
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-gray-100" />
            </div>
          ))}
        </div>

        {/* Real content: questionnaire accordion — fades in once fresh data loads */}
        {!isRefreshing && (
          <div className="animate-fadeIn">
            <QuestionnaireAccordion 
              assessments={sortedAssessments}
              patientDataModel={patientDataModel}
              nextQuestionnaireIndex={nextQuestionnaireIndex}
              nextQuestionnaireRef={nextQuestionnaireRef}
              isInformant={false}
              userOrganisation={userOrganisation}
              showInformantSetup={showInformantSetup}
              setShowInformantSetup={setShowInformantSetup}
            />
          </div>
        )}
</div>

      {/* Informant Setup Modal */}
      <InformantSetupForm 
        isOpen={showInformantSetup} 
        onClose={() => setShowInformantSetup(false)}
        patientDataModel={patientDataModel}
      />

      
      {/* Manage Informants Modal */}
      <PatientManageInformantsForm 
        isOpen={showManageInformants} 
        onClose={() => setShowManageInformants(false)}
        patientDataModel={patientDataModel}
      />
      

      {/* All Forms Completed Feedback Modal */}
      <AllFormsCompletedFeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)}
        onFeedbackSubmitted={() => {
          // Don't close modal - let user close it manually from the thank you screen
          // The callback just ensures the dashboard state is updated
        }}
      />
      </div>
  );
}
